import { Box, createStyles, Text } from "@mantine/core";
import moment from "moment";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { decrypt, formatDate, formatTime, getActiveSlotForDate } from "../../utils";
import TableLoader from "../TableLoader";

const SLOT_ORDER = [
  "6AM - 10AM",
  "10AM - 2PM",
  "2PM - 6PM",
  "6PM - 10PM",
  "10PM - 2AM",
  "2AM - 6AM",
];

const STATUS_COLORS = {
  Taken: "#4CAF50",
  Missed: "#EF5350",
  Refused: "#90A4AE",
  Upcoming: "#e8e6e0",
  "Unable to Take": "#FFD600",
  "Out of Medication": "#1a1a1a",
  Discontinued: "#8D6E63",
  PRN: "#AB47BC",
};

const avatarPalette = [
  { bg: "#FAEEDA", color: "#854F0B" },
  { bg: "#E1F5EE", color: "#0F6E56" },
  { bg: "#EEEDFE", color: "#534AB7" },
  { bg: "#E6F1FB", color: "#185FA5" },
  { bg: "#FCEBEB", color: "#A32D2D" },
];

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "NA";

const getClientName = (client) =>
  decrypt(client?.name, false) ||
  decrypt(client?.name) ||
  client?.name ||
  "Resident";

const getForm3Med = (client, medicationId) => {
  const decrypted = decrypt(client?.form3);
  if (!Array.isArray(decrypted)) return null;
  return (
    decrypted.find((item) => Number(item?.id) === Number(medicationId)) || null
  );
};

const formatMonthLabel = (date) =>
  moment(date).isValid()
    ? moment(date).format("MMMM YYYY")
    : moment().format("MMMM YYYY");

const getDateRangeForMonth = (date) => {
  const base = moment(date).isValid() ? moment(date) : moment();
  return {
    start: base.clone().startOf("month"),
    end: base.clone().endOf("month"),
  };
};

const createMedicationDescriptor = (row) => {
  const strength = row?.form3?.medicationStrength || row?.dosage || "";
  const amount =
    row?.form3?.medicationadministered || row?.dosage
      ? `${row?.form3?.medicationadministered || row?.dosage} ${
          Number(row?.form3?.medicationadministered || row?.dosage) > 1
            ? "tabs"
            : "tab"
        }`
      : "";
  const route = row?.form3?.ingestionMode || row?.route || "";
  return [strength, amount, route].filter(Boolean).join(" · ");
};

const normalizeOutcome = (row) => {
  // New schema: medicationType is an array — use outcome (already resolved) or last entry status
  const rawType = row?.outcome || row?.medicationType;
  const status = Array.isArray(rawType)
    ? rawType[rawType.length - 1]?.status || "upcoming"
    : rawType;
  if (status === "PRN") return "PRN";
  if (status === "upcoming") {
    return !moment(row?.date).isBefore(moment(), "day") ? "Upcoming" : "Missed";
  }
  return status || "Taken";
};

const useStyles = createStyles(() => ({
  root: {
    width: "100%",
  },
  previewWrap: {
    background: "#f5f3ee",
    padding: "16px 0 24px",
  },
  page: {
    maxWidth: 1200,
    margin: "20px auto",
    background: "#fff",
    border: "0.5px solid #e4e0d8",
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
  },
  header: {
    padding: "14px 28px",
    borderBottom: "0.5px solid #e4e0d8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  brandLogo: {
    width: 28,
    height: 28,
    background: "#E6A817",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
  },
  brandName: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  brandSub: {
    fontSize: 9,
    color: "#888",
    marginTop: 1,
  },
  headerRight: {
    textAlign: "right",
    fontSize: 10,
    color: "#888",
  },
  headerRightTitle: {
    display: "block",
    fontSize: 12,
    color: "#1a1a1a",
    fontWeight: 700,
    marginBottom: 1,
  },
  clientStrip: {
    background: "#1e2533",
    padding: "18px 28px",
  },
  clientInner: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 16,
    paddingBottom: 14,
    borderBottom: "0.5px solid rgba(255,255,255,.1)",
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    background: "rgba(230,168,23,.15)",
    border: "1.5px solid rgba(230,168,23,.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    color: "#E6A817",
    flexShrink: 0,
  },
  clientName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#fff",
    marginBottom: 3,
  },
  clientMeta: {
    fontSize: 10,
    color: "rgba(255,255,255,.45)",
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
  },
  sep: {
    color: "rgba(255,255,255,.2)",
  },
  allergyTag: {
    fontSize: 9,
    fontWeight: 700,
    background: "rgba(220,38,38,.2)",
    color: "#fca5a5",
    padding: "2px 8px",
    borderRadius: 20,
    border: "1px solid rgba(220,38,38,.25)",
  },
  monthBadge: {
    fontSize: 10,
    fontWeight: 700,
    background: "#E6A817",
    color: "#fff",
    padding: "3px 10px",
    borderRadius: 20,
    display: "inline-block",
  },
  clientStats: {
    display: "flex",
    gap: 0,
    flexWrap: "wrap",
  },
  stat: {
    padding: "0 16px",
    borderRight: "0.5px solid rgba(255,255,255,.1)",
  },
  statFirst: {
    paddingLeft: 0,
  },
  statLast: {
    borderRight: "none",
  },
  statLabel: {
    fontSize: 8,
    color: "rgba(255,255,255,.3)",
    textTransform: "uppercase",
    letterSpacing: ".5px",
    marginBottom: 2,
  },
  statValue: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
  },
  body: {
    padding: "20px 28px 28px",
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#888",
    marginBottom: 8,
  },
  staffRow: {
    marginBottom: 16,
  },
  staffCard: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    marginRight: 10,
    marginBottom: 6,
    padding: "6px 10px",
    border: "0.5px solid #e4e0d8",
    borderRadius: 7,
    background: "#fff",
  },
  legendRow: {
    padding: "10px 14px",
    background: "#f5f3ee",
    border: "0.5px solid #e4e0d8",
    borderRadius: 7,
    marginBottom: 14,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
  },
  legendLabel: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#888",
    marginRight: 12,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginRight: 12,
    marginBottom: 4,
    fontSize: 11,
    color: "#444",
  },
  legendSwatch: {
    width: 13,
    height: 13,
    borderRadius: 3,
    display: "inline-block",
    flexShrink: 0,
  },
  tableWrap: {
    overflowX: "auto",
    border: "0.5px solid #ddd",
    borderRadius: 7,
  },
  footer: {
    padding: "12px 28px",
    borderTop: "0.5px solid #e4e0d8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f5f3ee",
  },
  footerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  footerLogo: {
    width: 16,
    height: 16,
    background: "#E6A817",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 8,
    fontWeight: 700,
  },
  footerName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#6b6560",
  },
  footerSub: {
    fontSize: 9,
    color: "#b4b2a9",
    marginTop: 1,
  },
  commentsTable: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 20,
  },
}));

const MARReportViewTable = forwardRef(
  ({ data, filteredMar, setFilteredMar, loading }, ref) => {
    const { classes, cx } = useStyles();
    const { allSignature } = useSelector((state) => state?.signature);
    const [headers, setHeaders] = useState([]);

    useImperativeHandle(ref, () => ({
      applyFilters,
    }));

    useEffect(() => {
      if (!data?.source?.length) return;

      const formattedSource = [];

      data.source.forEach((item) => {
        const form3 = getForm3Med(item?.client, item?.medicationId);
        const baseMed = {
          ...item,
          form3,
          clientName: getClientName(item?.client),
          medicationName:
            form3?.medicationName || item?.medicationName || "-",
          dosage: item?.dosage || form3?.medicationStrength || "-",
          route: form3?.ingestionMode || "-",
          narcotic: form3?.isNarcotic || "-",
          remainQty:
            item?.medicineRemaining || form3?.medicineRemaining || "-",
          administrationType: form3?.frequency || "-",
        };

        // New schema: medicationType is an array of {date, status, timeSlot, staff, comments}
        if (
          Array.isArray(item?.medicationType) &&
          item.medicationType.length > 0
        ) {
          item.medicationType.forEach((entry) => {
            if (!entry?.date) return;
            const entryStaff = entry.staff || item?.staff;
            formattedSource.push({
              ...baseMed,
              date: entry.date,
              medicationType: entry.status || "upcoming",
              timeSlot: entry.timeSlot || getActiveSlotForDate(item?.timeSlot, entry.date) || "-",
              schedule: entry.timeSlot || getActiveSlotForDate(item?.timeSlot, entry.date) || "-",
              staffName: entryStaff?.name || "-",
              staffId: entryStaff?._id || entryStaff?.id || "-",
              administeredTime: entry.date ? formatTime(entry.date) : "-",
              outcome: entry.status || "upcoming",
              comments: entry.comments || item?.comments || "",
              reason: item?.reason || "",
              otherReason: item?.otherReason || "",
              wasMedicineEffective: item?.wasMedicineEffective || "",
            });
          });
        } else if (
          typeof item?.medicationType === "string" &&
          item?.medicationType
        ) {
          // Old schema fallback: medicationType is a plain string
          formattedSource.push({
            ...baseMed,
            schedule: item?.timeSlot || "-",
            staffName: item?.staff?.name || "-",
            staffId: item?.staff?.id || item?.staff?._id || "-",
            administeredTime: item?.staff
              ? formatTime(item?.createdAt)
              : "-",
            outcome: normalizeOutcome(item),
            comments: item?.comments || "",
            reason: item?.reason || "",
            otherReason: item?.otherReason || "",
            wasMedicineEffective: item?.wasMedicineEffective || "",
          });
        }
      });

      const sourceRows = formattedSource.sort(
        (a, b) =>
          new Date(a?.date || a?.createdAt) -
          new Date(b?.date || b?.createdAt),
      );

      setHeaders({
        headers: data?.fields,
        source: sourceRows,
      });
    }, [data]);

    const applyFilters = (filters) => {
      if (!headers?.source) return;
      if (filters === "clear") {
        setFilteredMar?.(null);
        return;
      }
      setFilteredMar?.(headers.source);
    };

    const sourceRows = filteredMar?.length
      ? filteredMar
      : headers?.source || [];

    const reportMonthDate =
      sourceRows?.[0]?.date ||
      sourceRows?.[0]?.createdAt ||
      moment().toISOString();
    const reportMonthLabel = formatMonthLabel(reportMonthDate);
    const { start, end } = getDateRangeForMonth(reportMonthDate);
    const reportRows = useMemo(
      () =>
        sourceRows.filter((row) =>
          moment(row?.date || row?.createdAt).isBetween(
            start,
            end,
            "day",
            "[]",
          ),
        ),
      [sourceRows, start, end],
    );
    const client = sourceRows?.[0]?.client;
    const clientForm1 = client?.form1 || {};
    const facilityName =
      client?.facility?.name ||
      sourceRows?.[0]?.facility?.name ||
      client?.business?.companyName ||
      "Facility";

    const staffList = Array.from(
      new Map(
        reportRows
          .filter((row) => row?.staffName && row?.staffId)
          .map((row) => [
            row.staffId,
            { id: row.staffId, name: row.staffName },
          ]),
      ).values(),
    );

    const medicationGroups = useMemo(() => {
      const grouped = new Map();

      reportRows
        .filter((row) => row?.medicationType !== "PRN")
        .forEach((row) => {
          const key = row?.medicationId || row?.medicationName;
          if (!grouped.has(key)) {
            grouped.set(key, {
              medicationId: row?.medicationId || key,
              medicationName: row?.medicationName || "Medication",
              descriptor: createMedicationDescriptor(row),
              physician: client?.facilityphysician || client?.physician || "—",
              pharmacy:
                row?.form3?.pharmacy ||
                client?.pharmacy?.name ||
                client?.pharmacy ||
                "—",
              slots: Array.from(
                new Set(
                  reportRows
                    .filter(
                      (item) =>
                        (item?.medicationId || item?.medicationName) === key &&
                        item?.medicationType !== "PRN" &&
                        item?.timeSlot,
                    )
                    .map((item) => item.timeSlot),
                ),
              ).sort((a, b) => SLOT_ORDER.indexOf(a) - SLOT_ORDER.indexOf(b)),
              rowsByDate: {},
            });
          }

          const group = grouped.get(key);
          const dateKey = moment(row?.date).format("YYYY-MM-DD");
          const slot = row?.timeSlot || "Unscheduled";
          if (!group.rowsByDate[slot]) {
            group.rowsByDate[slot] = {};
          }
          group.rowsByDate[slot][dateKey] = row;
        });

      return Array.from(grouped.values());
    }, [reportRows, client]);

    const commentRows = useMemo(() => {
      return reportRows
        .filter(
          (row) =>
            row?.comments && row?.staffName && row?.outcome !== "Upcoming",
        )
        .sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
    }, [reportRows]);

    const prnRows = useMemo(() => {
      return reportRows
        .filter((row) => row?.medicationType === "PRN" && row?.staffName)
        .sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt));
    }, [reportRows]);

    const monthDates = [];
    let cursor = start.clone();
    while (cursor <= end) {
      monthDates.push(cursor.clone());
      cursor.add(1, "day");
    }

    const renderOutcomeCell = (row, dateIndex) => {
      const outcome = row?.outcome || "Upcoming";
      const bg = STATUS_COLORS[outcome] || "#e8e6e0";
      const alt = dateIndex % 2 === 1 ? "#fafaf8" : "#fff";
      const isWeekend = [0, 6].includes(moment(row?.date).day());
      return (
        <td
          key={`${row?._id || row?.createdAt}-${dateIndex}`}
          style={{
            background:
              outcome === "Upcoming" ? (isWeekend ? "#f9f8f4" : alt) : bg,
            border: "0.5px solid #e0ddd8",
            height: 19,
            width: 22,
            textAlign: "center",
            fontSize: 7,
            fontWeight: 700,
            color: outcome === "Out of Medication" ? "#fff" : "#1a1a1a",
          }}
        >
          {outcome === "Discontinued"
            ? "DC"
            : outcome === "Upcoming"
              ? ""
              : getInitials(row?.staffName)}
        </td>
      );
    };

    const renderMainPage = () => (
      <div className="mar-report-page">
        <div className={classes.page}>
          <div className={classes.header}>
            <div className={classes.brand}>
              <div className={classes.brandLogo}>O</div>
              <div>
                <div className={classes.brandName}>Ogiso</div>
                <div className={classes.brandSub}>Electronic Health Record</div>
              </div>
            </div>
            <div className={classes.headerRight}>
              <strong className={classes.headerRightTitle}>
                Medication Administration Record (MAR)
              </strong>
              {facilityName}
            </div>
          </div>

          <div className={classes.clientStrip}>
            <div className={classes.clientInner}>
              <div className={classes.avatar}>
                {getInitials(getClientName(client))}
              </div>
              <div style={{ flex: 1 }}>
                <div className={classes.clientName}>
                  {getClientName(client)}
                </div>
                <div className={classes.clientMeta}>
                  <span>{clientForm1?.gender || "—"}</span>
                  <span className={classes.sep}>·</span>
                  <span>
                    DOB:{" "}
                    {clientForm1?.dob
                      ? moment(clientForm1.dob).format("MM/DD/YYYY")
                      : "—"}
                  </span>
                  <span className={classes.sep}>·</span>
                  <span>{facilityName}</span>
                  <span className={classes.sep}>·</span>
                  <span className={classes.allergyTag}>
                    Allergies:{" "}
                    {clientForm1?.knownAllergies?.filter(Boolean)?.join(", ") ||
                      "None"}
                  </span>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 8,
                    color: "rgba(255,255,255,.3)",
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                    marginBottom: 4,
                  }}
                >
                  Report month
                </div>
                <span className={classes.monthBadge}>{reportMonthLabel}</span>
                <div
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,.35)",
                    marginTop: 5,
                  }}
                >
                  {start.format("MM/DD/YYYY")} – {end.format("MM/DD/YYYY")}
                </div>
              </div>
            </div>

            <div className={classes.clientStats}>
              {[
                ["Client", getClientName(client)],
                [
                  "DOB",
                  clientForm1?.dob
                    ? moment(clientForm1.dob).format("MM/DD/YYYY")
                    : "—",
                ],
                ["Medications", `${medicationGroups.length}`],
                [
                  "Physician",
                  client?.facilityphysician || client?.physician || "—",
                ],
                ["Pharmacy", client?.pharmacy?.name || client?.pharmacy || "—"],
              ].map(([label, value], index, arr) => (
                <div
                  key={label}
                  className={cx(
                    classes.stat,
                    index === 0 && classes.statFirst,
                    index === arr.length - 1 && classes.statLast,
                  )}
                >
                  <div className={classes.statLabel}>{label}</div>
                  <div className={classes.statValue}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className={classes.body}>
            <div className={classes.staffRow}>
              <div className={classes.sectionLabel}>Contributing staff</div>
              {staffList.map((staff, index) => {
                const palette = avatarPalette[index % avatarPalette.length];
                return (
                  <div key={staff.id} className={classes.staffCard}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: palette.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        fontWeight: "bold",
                        color: palette.color,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(staff.name)}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#1a1a1a",
                        }}
                      >
                        {staff.name}
                      </div>
                      <div style={{ fontSize: 9, color: "#888" }}>
                        Initial: {getInitials(staff.name)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={classes.legendRow}>
              <span className={classes.legendLabel}>Status</span>
              {Object.entries(STATUS_COLORS).map(([label, color]) => (
                <span key={label} className={classes.legendItem}>
                  <span
                    className={classes.legendSwatch}
                    style={{ background: color }}
                  />
                  {label}
                </span>
              ))}
            </div>

            <div className={classes.tableWrap}>
              <table
                className="mar-main-table"
                style={{ borderCollapse: "collapse", tableLayout: "fixed" }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        background: "#1e2533",
                        color: "#fff",
                        fontSize: 8,
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: ".3px",
                        padding: "5px 8px",
                        width: 120,
                        textAlign: "left",
                        verticalAlign: "top",
                      }}
                    >
                      Medication
                    </th>
                    <th
                      style={{
                        background: "#f0eeea",
                        color: "#666",
                        fontSize: 7,
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        letterSpacing: ".3px",
                        padding: "3px 6px",
                        textAlign: "left",
                        width: 66,
                      }}
                    >
                      Time slot
                    </th>
                    {monthDates.map((date) => {
                      const isWeekend = [0, 6].includes(date.day());
                      return (
                        <th
                          key={date.format("YYYY-MM-DD")}
                          style={{
                            width: 22,
                            background: isWeekend ? "#e8e4da" : "#f0eeea",
                            textAlign: "center",
                            padding: "1px 0",
                            border: "0.5px solid #ddd",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 6,
                              color: isWeekend ? "#777" : "#999",
                              display: "block",
                            }}
                          >
                            {date.format("dd")}
                          </span>
                          <span
                            style={{
                              fontSize: 8,
                              fontWeight: "bold",
                              color: "#1a1a1a",
                              display: "block",
                            }}
                          >
                            {date.format("D")}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {medicationGroups.map((med, medIndex) => {
                    const slots = med.slots.length ? med.slots : ["6AM - 10AM"];

                    return slots.map((slot, slotIndex) => (
                      <tr key={`${med.medicationId}-${slot}`}>
                        {slotIndex === 0 ? (
                          <td
                            rowSpan={slots.length}
                            style={{
                              background:
                                medIndex % 2 === 0 ? "#fff" : "#fafaf8",
                              border: "0.5px solid #e0ddd8",
                              padding: "8px 8px",
                              width: 120,
                              verticalAlign: "top",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#1a1a1a",
                                marginBottom: 4,
                              }}
                            >
                              {med.medicationName}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: "#6b6560",
                                lineHeight: 1.35,
                              }}
                            >
                              {med.descriptor || "—"}
                            </div>
                          </td>
                        ) : null}
                        <td
                          style={{
                            fontSize: 8,
                            color: "#555",
                            padding: "2px 6px",
                            background: medIndex % 2 === 0 ? "#fff" : "#fafaf8",
                            whiteSpace: "nowrap",
                            width: 66,
                            border: "0.5px solid #e0ddd8",
                          }}
                        >
                          {slot}
                        </td>
                        {monthDates.map((date, dateIndex) => {
                          const row =
                            med.rowsByDate?.[slot]?.[date.format("YYYY-MM-DD")];
                          const alt = medIndex % 2 === 0 ? "#fff" : "#fafaf8";
                          const isWeekend = [0, 6].includes(date.day());
                          return row ? (
                            renderOutcomeCell(row, dateIndex)
                          ) : (
                            <td
                              key={`${slot}-${date.format("YYYY-MM-DD")}`}
                              style={{
                                background: isWeekend ? "#f9f8f4" : alt,
                                border: "0.5px solid #e0ddd8",
                                height: 19,
                                width: 22,
                              }}
                            />
                          );
                        })}
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className={classes.footer}>
            <div className={classes.footerLeft}>
              <div className={classes.footerLogo}>O</div>
              <div>
                <div className={classes.footerName}>
                  Generated using Ogiso EHR
                </div>
                <div className={classes.footerSub}>
                  {facilityName} · Confidential
                </div>
              </div>
            </div>
            <div style={{ fontSize: 9, color: "#b4b2a9", textAlign: "right" }}>
              Generated {moment().format("MM/DD/YYYY")} · {reportMonthLabel} MAR
            </div>
          </div>
        </div>
      </div>
    );

    const renderCommentsPage = () => (
      <div className="mar-report-page">
        <div className={classes.page}>
          <div className={classes.header}>
            <div className={classes.brand}>
              <div className={classes.brandLogo}>O</div>
              <div>
                <div className={classes.brandName}>Ogiso</div>
                <div className={classes.brandSub}>Electronic Health Record</div>
              </div>
            </div>
            <div className={classes.headerRight}>
              <strong className={classes.headerRightTitle}>
                MAR Administration Comments
              </strong>
              {getClientName(client)} · {reportMonthLabel}
            </div>
          </div>

          <div className={classes.body}>
            <div className={classes.sectionLabel}>
              Medication administration comments — {reportMonthLabel}
            </div>

            <table className={classes.commentsTable}>
              <thead>
                <tr
                  style={{
                    background: "#f5f3ee",
                    borderBottom: "0.5px solid #e4e0d8",
                  }}
                >
                  {[
                    ["Date", 90],
                    ["Time", 70],
                    ["Medication", 160],
                    ["Status", 80],
                    ["Comment", null],
                    ["Staff", 80],
                  ].map(([label, width]) => (
                    <th
                      key={label}
                      style={{
                        padding: "8px 12px",
                        textAlign: "left",
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#888",
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                        width: width || undefined,
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commentRows.length ? (
                  commentRows.map((row, index) => {
                    const statusBg = STATUS_COLORS[row.outcome] || "#F3F4F6";
                    const statusColor =
                      row.outcome === "Taken"
                        ? "#166534"
                        : row.outcome === "PRN"
                          ? "#7e22ce"
                          : row.outcome === "Missed"
                            ? "#A32D2D"
                            : "#854F0B";

                    return (
                      <tr
                        key={`${row?._id || row?.createdAt}-${index}`}
                        style={{
                          borderBottom: "0.5px solid #f0eeea",
                          background: index % 2 === 1 ? "#fafaf8" : "#fff",
                        }}
                      >
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 12,
                            color: "#1a1a1a",
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(row.date)}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 11,
                            color: "#6b6560",
                          }}
                        >
                          {row.schedule || formatTime(row.createdAt)}
                        </td>
                        <td style={{ padding: "9px 12px" }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#1a1a1a",
                            }}
                          >
                            {row.medicationName}
                          </div>
                          <div style={{ fontSize: 10, color: "#888" }}>
                            {createMedicationDescriptor(row)}
                          </div>
                        </td>
                        <td style={{ padding: "9px 12px" }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              background: `${statusBg}22`,
                              color: statusColor,
                              padding: "2px 8px",
                              borderRadius: 20,
                            }}
                          >
                            {row.outcome}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 11,
                            color: "#1a1a1a",
                            lineHeight: 1.5,
                          }}
                        >
                          {row.comments}
                        </td>
                        <td
                          style={{
                            padding: "9px 12px",
                            fontSize: 11,
                            color: "#6b6560",
                          }}
                        >
                          {row.staffName}
                          <br />
                          <span style={{ fontSize: 10, color: "#888" }}>
                            {getInitials(row.staffName)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      style={{ padding: 16, color: "#888", fontSize: 11 }}
                    >
                      No medication administration comments recorded for this
                      month.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={classes.footer}>
            <div className={classes.footerLeft}>
              <div className={classes.footerLogo}>O</div>
              <div>
                <div className={classes.footerName}>
                  Generated using Ogiso EHR
                </div>
                <div className={classes.footerSub}>
                  {facilityName} · Confidential
                </div>
              </div>
            </div>
            <div style={{ fontSize: 9, color: "#b4b2a9", textAlign: "right" }}>
              MAR Administration Comments · {reportMonthLabel}
            </div>
          </div>
        </div>
      </div>
    );

    const renderPRNPage = () => (
      <div className="mar-report-page">
        <div className={classes.page}>
          <div className={classes.header}>
            <div className={classes.brand}>
              <div className={classes.brandLogo}>O</div>
              <div>
                <div className={classes.brandName}>Ogiso</div>
                <div className={classes.brandSub}>Electronic Health Record</div>
              </div>
            </div>
            <div className={classes.headerRight}>
              <strong className={classes.headerRightTitle}>
                PRN Medication Administration
              </strong>
              {getClientName(client)} · {reportMonthLabel}
            </div>
          </div>

          <div className={classes.body}>
            <div className={classes.sectionLabel}>
              PRN administrations — {reportMonthLabel}
            </div>

            <table className={classes.commentsTable}>
              <thead>
                <tr
                  style={{
                    background: "#f5f3ee",
                    borderBottom: "0.5px solid #e4e0d8",
                  }}
                >
                  {[
                    "Date",
                    "Time",
                    "Medication",
                    "Reason",
                    "Comment",
                    "Staff",
                  ].map((label) => (
                    <th
                      key={label}
                      style={{
                        padding: "8px 12px",
                        textAlign: "left",
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#888",
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prnRows.map((row, index) => (
                  <tr
                    key={`${row?._id || row?.createdAt}-${index}`}
                    style={{
                      borderBottom: "0.5px solid #f0eeea",
                      background: index % 2 === 1 ? "#fafaf8" : "#fff",
                    }}
                  >
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 12,
                        color: "#1a1a1a",
                        fontWeight: 500,
                      }}
                    >
                      {formatDate(row.date || row.createdAt)}
                    </td>
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 11,
                        color: "#6b6560",
                      }}
                    >
                      {formatTime(row.createdAt)}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#1a1a1a",
                        }}
                      >
                        {row.medicationName}
                      </div>
                      <div style={{ fontSize: 10, color: "#888" }}>
                        {createMedicationDescriptor(row)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 11,
                        color: "#1a1a1a",
                        lineHeight: 1.5,
                      }}
                    >
                      {row.reason === "Other"
                        ? row.otherReason || "Other"
                        : row.reason || "—"}
                    </td>
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 11,
                        color: "#1a1a1a",
                        lineHeight: 1.5,
                      }}
                    >
                      {row.comments || "—"}
                    </td>
                    <td
                      style={{
                        padding: "9px 12px",
                        fontSize: 11,
                        color: "#6b6560",
                      }}
                    >
                      {row.staffName}
                      <br />
                      <span style={{ fontSize: 10, color: "#888" }}>
                        {getInitials(row.staffName)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={classes.footer}>
            <div className={classes.footerLeft}>
              <div className={classes.footerLogo}>O</div>
              <div>
                <div className={classes.footerName}>
                  Generated using Ogiso EHR
                </div>
                <div className={classes.footerSub}>
                  {facilityName} · Confidential
                </div>
              </div>
            </div>
            <div style={{ fontSize: 9, color: "#b4b2a9", textAlign: "right" }}>
              PRN Medication Administration · {reportMonthLabel}
            </div>
          </div>
        </div>
      </div>
    );

    const hiddenRows = reportRows.map((row) => (
      <tr key={`${row?._id || row?.createdAt}-${row.staffId}`}>
        <td>{formatDate(row.date)}</td>
        <td>{row.clientName}</td>
        <td>{row.medicationName}</td>
        <td>{row.dosage}</td>
        <td>{row.route}</td>
        <td>{row.narcotic}</td>
        <td>{row.remainQty}</td>
        <td>{row.administrationType}</td>
        <td>{row.schedule}</td>
        <td>{row.administeredTime}</td>
        <td>{row.staffName}</td>
        <td>{row.outcome}</td>
      </tr>
    ));

    return (
      <div className={classes.root}>
        {loading ? (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
            }}
          >
            <TableLoader />
          </Box>
        ) : (
          <>
            <div id="sampleTable" className={classes.previewWrap}>
              {renderMainPage()}
              {renderCommentsPage()}
              {prnRows.length ? renderPRNPage() : null}
            </div>

            <table
              id="hiddenTable"
              style={{
                width: "100%",
                border: "1px solid #EDEDED",
                position: "absolute",
                top: "-10000000px",
                left: "-10000000px",
              }}
            >
              <tbody>
                <tr style={{ background: "#000", color: "#fff" }}>
                  {headers?.headers?.map((header, index) => (
                    <th key={index}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "150px",
                          gap: "1rem",
                          padding: "1rem",
                        }}
                      >
                        {header}
                      </span>
                    </th>
                  ))}
                </tr>
                {hiddenRows}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  },
);

export default MARReportViewTable;
